import { defineStore } from "pinia";
import type { TatOrder } from "~/types/apps/TatType";

function today(): string {
    // Fecha local del navegador en formato YYYY-MM-DD, para el <input type="date">.
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export const useTatOrdersStore = defineStore({
    id: "tatOrders",
    state: () => ({
        orders: [] as TatOrder[],
        count: 0,
        page: 1,
        pageSize: 20,
        statusFilter: null as number | null,
        dateFrom: today() as string,
        dateTo: today() as string,
        loading: false,
        // Conteo de pedidos YA ENVIADOS hoy, para el badge de "Pedidos" en el
        // menú inferior — independiente de `count`/`orders` (que reflejan el
        // filtro que el vendedor tenga puesto en la lista de Pedidos.vue).
        todayCount: 0,
    }),
    actions: {
        async fetchTodayCount() {
            const baseUrl = useRuntimeConfig().public.apiBase;
            try {
                const params = new URLSearchParams({
                    page_size: "1",
                    date_from: today(),
                    date_to: today(),
                });
                const response = await fetchWrapper.get(
                    `${baseUrl}/api/v1/tat/orders/?${params.toString()}`,
                );
                this.todayCount = response.count ?? 0;
                return this.todayCount;
            } catch (error) {
                console.log(error);
            }
        },
        async fetchOrders(page: number = this.page) {
            const baseUrl = useRuntimeConfig().public.apiBase;
            this.page = page;
            this.loading = true;
            try {
                const params = new URLSearchParams({
                    page: String(this.page),
                    page_size: String(this.pageSize),
                });
                if (this.statusFilter !== null) params.set("status", String(this.statusFilter));
                if (this.dateFrom) params.set("date_from", this.dateFrom);
                if (this.dateTo) params.set("date_to", this.dateTo);
                const response = await fetchWrapper.get(
                    `${baseUrl}/api/v1/tat/orders/?${params.toString()}`,
                );
                this.orders = response.results ?? response;
                this.count = response.count ?? this.orders.length;
                return this.orders;
            } catch (error) {
                console.log(error);
            } finally {
                this.loading = false;
            }
        },
        setStatusFilter(status: number | null) {
            this.statusFilter = status;
        },
        setDateRange(dateFrom: string, dateTo: string) {
            this.dateFrom = dateFrom;
            this.dateTo = dateTo;
        },
        async fetchOrder(id: number): Promise<TatOrder> {
            const baseUrl = useRuntimeConfig().public.apiBase;
            return await fetchWrapper.get(`${baseUrl}/api/v1/tat/orders/${id}/`);
        },
        async registerPayment(orderId: number, paymentMethod: string, amountReceived: number, voucherFile?: File | null): Promise<TatOrder> {
            const baseUrl = useRuntimeConfig().public.apiBase;
            const formData = new FormData();
            formData.append('order_id', String(orderId));
            formData.append('payment_method', paymentMethod);
            formData.append('amount_received', String(amountReceived));
            if (voucherFile) {
                formData.append('payment_voucher', voucherFile);
            }
            return await fetchWrapper.post(
                `${baseUrl}/api/v1/tat/orders/${orderId}/register_payment/`,
                formData,
            );
        },
    },
});
